import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    IHttpConnectionOptions,
} from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import { NotificationEventType } from 'lemunzo-api';
import { PrimeNGToastSeverity } from '../enums/primeng-toast-severity';

@Injectable({
    providedIn: 'root',
})
/**
 * Class to provide SignalRService functionalities.
 */
export class SignalRService {
    private readonly receiveNotificationHandlers = new Map<
        string,
        SignalRReceiveNotificationHandler
    >();
    private readonly receiveNotificationMethodName = 'ReceiveNotification';
    private notificationHubConnection?: HubConnection;

    constructor(private readonly authService: MsalService) {}

    /**
     * Converts SignalRService notification severity to toast severity.
     *
     * @param notificationSeverity SignalRService notification severity to convert.
     * @returns PrimeNG toast severity.
     */
    public static getToastSeverity(
        notificationSeverity: SignalRNotificationSeverity
    ): PrimeNGToastSeverity {
        switch (notificationSeverity) {
            case SignalRNotificationSeverity.WARNING:
                return PrimeNGToastSeverity.WARN;
            case SignalRNotificationSeverity.ERROR:
                return PrimeNGToastSeverity.ERROR;
            case SignalRNotificationSeverity.SUCCESS:
                return PrimeNGToastSeverity.SUCCESS;
            default:
                return PrimeNGToastSeverity.INFO;
        }
    }

    /**
     * Initializes and starts all the SignalRService hub connections.
     *
     * @param apiUrl URL to the BE server
     * @param scopes MSAL scopes to request access token with
     */
    public start(apiUrl: string, scopes: string[]): void {
        this.getUserAccessToken(scopes).then((token) => {
            this.startNotificationHub(apiUrl, token);
        });
    }

    /**
     * Stops all the SignalRService hub connections.
     */
    public stop(): void {
        this.stopNotificationHub();
    }

    /**
     * Registers callback function to handle the received notification event.
     *
     * @param handler Callback function to invoke on event.
     * @returns The handler identifier.
     */
    public registerReceiveNotificationHandler(
        handler: SignalRReceiveNotificationHandler
    ): string {
        const handlerIdentifier = `${this.receiveNotificationMethodName}_${Math.random()}`;

        this.receiveNotificationHandlers.set(handlerIdentifier, handler);

        return handlerIdentifier;
    }

    /**
     * Unregisters callback function to handle the received notification event.
     *
     * @param handlerIdentifier Handler identifier to unregister.
     */
    public unregisterReceiveNotificationHandler(
        handlerIdentifier: string
    ): void {
        this.receiveNotificationHandlers.delete(handlerIdentifier);
    }

    /**
     * Starts the notification hub connection.
     *
     * @param userAuthToken Current user authentication token.
     * @param apiUrl URL of the BE server
     */
    private startNotificationHub(apiUrl: string, userAuthToken?: string): void {
        if (this.notificationHubConnection) {
            return;
        }

        const options: IHttpConnectionOptions = {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
        };

        if (userAuthToken && userAuthToken.trim().length !== 0) {
            options.accessTokenFactory = () => userAuthToken.trim();
        }

        this.notificationHubConnection = new HubConnectionBuilder()
            .withUrl(`${apiUrl.replace('/api', '')}hubs/notification`, options)
            .build();

        this.notificationHubConnection
            .start()
            .then(() => {
                this.notificationHubConnection!.on(
                    this.receiveNotificationMethodName,
                    (message: SignalRNotificationHubMessage) => {
                        this.receiveNotificationHandlers.forEach((handler) =>
                            handler(message)
                        );
                    }
                );
            })
            .catch(() => {
                this.notificationHubConnection = undefined;
            });
    }

    /**
     * Stops the notification hub connection.
     */
    private stopNotificationHub(): void {
        if (!this.notificationHubConnection) {
            return;
        }

        this.notificationHubConnection.stop().then(() => {
            this.notificationHubConnection = undefined;
        });
    }

    /**
     * Retrieves current user authentication token.
     *
     * @returns Authentication token.
     */
    private async getUserAccessToken(
        scopes: string[]
    ): Promise<string | undefined> {
        const account =
            this.authService.instance.getActiveAccount() ??
            this.authService.instance.getAllAccounts()[0];

        if (!account) return undefined;

        try {
            const result = await firstValueFrom(
                this.authService.acquireTokenSilent({
                    account,
                    scopes,
                })
            );
            return result.accessToken;
        } catch {
            return undefined;
        }
    }
}

/**
 * Represents an object for a SignalRService notification hub message sent from the server.
 */
interface SignalRNotificationHubMessage {
    body: string;
    eventType: NotificationEventType;
    severity: SignalRNotificationSeverity;
    title: string;
}

/**
 * Enumeration representing different SignalRService notification severities.
 */
export enum SignalRNotificationSeverity {
    INFO = 0,
    WARNING = 1,
    ERROR = 2,
    SUCCESS = 3,
}

/**
 * Represents an object for a SignalRService receive notification event handler.
 */
type SignalRReceiveNotificationHandler = (
    message: SignalRNotificationHubMessage
) => void;
